import {
  apiErrorSchema,
  fieldCommandSchema,
  fieldSnapshotSchema,
  fieldVoiceTokenSchema,
} from '@mio/shared';

import { ApiRequestError } from '@/shared/api/api-error';
import { apiRequest } from '@/shared/api/client';
import { notifyUsageError } from '@/shared/api/usage-events';
import { env } from '@/shared/lib/env';
import { supabase } from '@/shared/lib/supabase';

import type { FieldCommand, FieldDocument } from '@mio/shared';

export const loadField = (signal?: AbortSignal) =>
  apiRequest('/field', fieldSnapshotSchema, signal ? { signal } : {});
export const sendFieldCommand = (input: FieldCommand) => {
  const command = fieldCommandSchema.parse(input);
  if (new TextEncoder().encode(JSON.stringify(command)).byteLength > 1_500_000)
    throw new Error(
      'This request exceeds 1.5 MB including source and metadata. Choose a smaller candidate.',
    );
  return apiRequest('/field/commands', fieldSnapshotSchema, {
    method: 'POST',
    body: command,
  });
};
export const fieldVoiceToken = () =>
  apiRequest('/field/voice/token', fieldVoiceTokenSchema, { method: 'POST' });

async function fieldResponse(path: string, options?: { body: unknown; signal?: AbortSignal }) {
  const { data } = await supabase.auth.getSession();
  const response = await fetch(`${env.VITE_API_URL}/api/v1/field${path}`, {
    method: options ? 'POST' : 'GET',
    headers: {
      ...(data.session ? { Authorization: `Bearer ${data.session.access_token}` } : {}),
      ...(options ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(options ? { body: JSON.stringify(options.body) } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  });
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));
    notifyUsageError(parsed.success ? parsed.data.error.code : undefined);
    throw new ApiRequestError(
      response.status,
      parsed.success ? parsed.data.error.code : 'field_download_failed',
      parsed.success
        ? parsed.data.error.message
        : 'Could not retrieve this file. Please try again.',
    );
  }
  return response;
}

export async function fieldBlob(path: string, options?: { body: unknown; signal?: AbortSignal }) {
  return (await fieldResponse(path, options)).blob();
}

/** Preserve the authenticated response body so playback can start before synthesis ends. */
export function fieldSpeechStream(messageId: string, signal: AbortSignal) {
  return fieldResponse('/voice/speech', { body: { messageId }, signal });
}

export async function fieldSpeech(messageId: string, signal: AbortSignal) {
  return (await fieldSpeechStream(messageId, signal)).blob();
}

export async function downloadFieldAsset(
  document: FieldDocument | null,
  format: FieldDocument['formats'][number] = 'markdown',
) {
  if (document && !document.formats.includes(format))
    throw new Error('This representation is not ready to download.');
  const blob = await fieldBlob(
    document
      ? `/documents/${encodeURIComponent(document.id)}/download?format=${format}`
      : '/starter',
  );
  const extensions = {
    markdown: 'md',
    html: 'html',
    json: 'json',
    yaml: 'yaml',
    csv: 'csv',
    pdf: 'pdf',
  };
  const filename = document
    ? `${document.title.replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 100) || 'document'}-v${document.version}.${extensions[format]}`
    : 'ladder-starter.tar.gz';
  const href = URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = href;
  link.download = filename;
  link.click();
  // Keep the URL alive until the browser has consumed the download.
  setTimeout(() => URL.revokeObjectURL(href), 30_000);
}
